import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DiagnosticsHealthCard } from "@/components/admin/DiagnosticsHealthCard";
import { ReleaseRegressionPanel } from "@/components/admin/ReleaseRegressionPanel";
import { IncidentDetailsSheet, INCIDENT_STATUSES } from "@/components/admin/IncidentDetailsSheet";
import { PERF_THRESHOLDS } from "@/lib/diagnostics/types";
import { SECTIONS, rowInSection, sectionByKey } from "@/lib/diagnostics/sections";
import { buildSafeFixPrompt, buildInvestigationPrompt } from "@/lib/diagnostics/incident-prompts";
import type { IncidentRecord } from "@/lib/diagnostics/evidence";
import { AlertTriangle, Clock, Copy, Gauge, RefreshCw, Search, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/admin/diagnostics")({
  component: DiagnosticsPage,
  head: () => ({
    meta: [
      { title: "Diagnostics & Incidents | Brothers Club Admin" },
      {
        name: "description",
        content:
          "Incident investigation, stage timings, duplicate detection, protected-function guard and controlled fix prompts for Brothers Club.",
      },
      { property: "og:title", content: "Diagnostics & Incidents | Brothers Club Admin" },
      {
        property: "og:description",
        content: "Evidence-based incident investigation and safe fix prompt generation.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type DiagRow = IncidentRecord & {
  id: string;
  user_id: string | null;
  resolved: boolean;
  created_at: string;
};

type PerfRow = {
  id: string;
  metric: string;
  duration_ms: number;
  rating: string;
  page: string | null;
  app_version: string | null;
  platform: string | null;
  created_at: string;
};

type AlertRow = {
  id: string;
  alert_key: string;
  severity: string;
  title: string;
  detail: string | null;
  occurrences: number;
  affected_users: number;
  last_seen: string;
};

const RANGES = [
  { key: "24h", label: "Last 24 hours", hours: 24 },
  { key: "7d", label: "Last 7 days", hours: 24 * 7 },
  { key: "30d", label: "Last 30 days", hours: 24 * 30 },
] as const;

function sevClass(sev: string) {
  if (sev === "critical") return "bg-destructive/15 text-destructive border-destructive/30";
  if (sev === "error") return "bg-orange-500/15 text-orange-600 border-orange-500/30";
  if (sev === "warning") return "bg-amber-500/15 text-amber-600 border-amber-500/30";
  return "bg-muted text-muted-foreground border-border";
}

function ratingClass(rating: string) {
  if (rating === "critical") return "text-destructive";
  if (rating === "slow") return "text-amber-600";
  return "text-emerald-600";
}

function fmt(ts?: string | null) {
  return ts ? new Date(ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";
}

async function copy(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed — long-press to copy manually");
  }
}

/** Stable anonymous reference so an admin can group by user without seeing identity. */
function anonRef(userId?: string | null) {
  if (!userId) return "anon";
  return `U-${userId.replace(/-/g, "").slice(0, 6).toUpperCase()}`;
}

function DiagnosticsPage() {
  const qc = useQueryClient();
  const [sectionKey, setSectionKey] = useState<string>("live_errors");
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("24h");
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [version, setVersion] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showResolved, setShowResolved] = useState(false);
  const [selected, setSelected] = useState<DiagRow | null>(null);

  const since = useMemo(() => {
    const hours = RANGES.find((r) => r.key === range)?.hours ?? 24;
    return new Date(Date.now() - hours * 3600_000).toISOString();
  }, [range]);

  const section = sectionByKey(sectionKey);

  const eventsQ = useQuery({
    queryKey: ["diag-events", since, severity, platform, showResolved],
    queryFn: async () => {
      let q = supabase
        .from("diagnostics_events")
        .select("*")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(1000);
      if (severity !== "all") q = q.eq("severity", severity);
      if (platform !== "all") q = q.eq("platform", platform);
      if (!showResolved) q = q.eq("resolved", false);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as DiagRow[];
    },
    staleTime: 15_000,
  });

  const perfQ = useQuery({
    queryKey: ["diag-perf", since],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("perf_metrics")
        .select("*")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(1500);
      if (error) throw error;
      return (data ?? []) as unknown as PerfRow[];
    },
    staleTime: 30_000,
  });

  const alertsQ = useQuery({
    queryKey: ["diag-alerts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("diagnostics_alerts")
        .select("*")
        .eq("acknowledged", false)
        .order("last_seen", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as AlertRow[];
    },
    staleTime: 30_000,
  });

  const workflowQ = useQuery({
    queryKey: ["diag-workflows"],
    queryFn: async () => {
      const { data } = await supabase.from("diagnostics_fix_workflow").select("group_hash,status,recurred").limit(500);
      const map = new Map<string, { status: string; recurred: boolean }>();
      for (const w of data ?? []) map.set(w.group_hash, { status: w.status, recurred: w.recurred });
      return map;
    },
    staleTime: 30_000,
  });

  const versions = useMemo(() => {
    const set = new Set<string>();
    for (const r of eventsQ.data ?? []) if (r.app_version) set.add(r.app_version);
    return [...set].sort().reverse();
  }, [eventsQ.data]);

  const groups = useMemo(() => {
    const rows = (eventsQ.data ?? []).filter((r) => rowInSection(r, section));
    const term = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (version !== "all" && r.app_version !== version) return false;
      if (statusFilter !== "all") {
        const st = workflowQ.data?.get(r.group_hash ?? "")?.status ?? "open";
        if (st !== statusFilter) return false;
      }
      if (!term) return true;
      return [r.error_id, r.code, r.stage, r.route, r.page, r.safe_message, r.app_version, r.correlation_id, anonRef(r.user_id)]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
    const map = new Map<string, { latest: DiagRow; count: number; users: Set<string>; first: string }>();
    for (const r of filtered) {
      const key = r.group_hash ?? `${r.category}:${r.code}:${r.stage}`;
      const g = map.get(key);
      if (!g) map.set(key, { latest: r, count: 1, users: new Set(r.user_id ? [r.user_id] : []), first: r.created_at });
      else {
        g.count++;
        if (r.user_id) g.users.add(r.user_id);
        if (r.created_at < g.first) g.first = r.created_at;
      }
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [eventsQ.data, search, section, version, statusFilter, workflowQ.data]);

  const sectionMetrics = useMemo(() => {
    const rows = (perfQ.data ?? []).filter((r) => !section.metrics || section.metrics.includes(r.metric));
    const by = new Map<string, number[]>();
    for (const r of rows) {
      const arr = by.get(r.metric) ?? [];
      arr.push(r.duration_ms);
      by.set(r.metric, arr);
    }
    return [...by.entries()]
      .map(([metric, values]) => {
        const s = values.slice().sort((a, b) => a - b);
        const p = (q: number) => s[Math.min(s.length - 1, Math.floor(s.length * q))] ?? 0;
        const [slow, critical] = (PERF_THRESHOLDS as any)[metric] ?? [2000, 5000];
        const p95 = p(0.95);
        return {
          metric,
          samples: s.length,
          p50: p(0.5),
          p95,
          max: s[s.length - 1] ?? 0,
          rating: p95 >= critical ? "critical" : p95 >= slow ? "slow" : "healthy",
          threshold: `${slow} / ${critical}`,
        };
      })
      .sort((a, b) => b.p95 - a.p95);
  }, [perfQ.data, section]);

  const slowRows = useMemo(
    () =>
      (perfQ.data ?? [])
        .filter((r) => r.rating !== "healthy" && (!section.metrics || section.metrics.includes(r.metric)))
        .slice(0, 100),
    [perfQ.data, section],
  );

  function toIncident(g: { latest: DiagRow; count: number; first: string; users: Set<string> }): DiagRow {
    return { ...g.latest, occurrence_count: g.count, first_seen: g.first, affected_users: g.users.size };
  }

  async function toggleResolved(row: DiagRow) {
    const { error } = await supabase
      .from("diagnostics_events")
      .update({ resolved: !row.resolved, resolved_at: row.resolved ? null : new Date().toISOString() } as any)
      .eq("group_hash", row.group_hash ?? "")
      .gte("created_at", since);
    if (error) return toast.error("Could not update status");
    toast.success(row.resolved ? "Reopened" : "Marked resolved");
    qc.invalidateQueries({ queryKey: ["diag-events"] });
    setSelected(null);
  }

  async function acknowledgeAlert(a: AlertRow) {
    const { error } = await supabase
      .from("diagnostics_alerts")
      .update({ acknowledged: true, acknowledged_at: new Date().toISOString() } as any)
      .eq("id", a.id);
    if (error) return toast.error("Could not acknowledge");
    qc.invalidateQueries({ queryKey: ["diag-alerts"] });
  }

  return (
    <div className="w-full max-w-full space-y-4 overflow-x-hidden">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-black sm:text-2xl">
            <Gauge className="h-6 w-6 shrink-0 text-primary" /> Diagnostics &amp; Incidents
          </h1>
          <p className="text-sm text-muted-foreground">{section.purpose}</p>
        </div>
        <button
          onClick={() => {
            qc.invalidateQueries({ queryKey: ["diag-events"] });
            qc.invalidateQueries({ queryKey: ["diag-perf"] });
            qc.invalidateQueries({ queryKey: ["diag-alerts"] });
            qc.invalidateQueries({ queryKey: ["diag-workflows"] });
          }}
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </header>

      {(alertsQ.data?.length ?? 0) > 0 && (
        <section className="space-y-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3">
          <p className="flex items-center gap-2 text-sm font-black text-destructive">
            <AlertTriangle className="h-4 w-4" /> Active alerts ({alertsQ.data!.length})
          </p>
          {alertsQ.data!.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-background p-2">
              <div className="min-w-0">
                <p className="break-words text-sm font-semibold">{a.title}</p>
                <p className="break-words text-xs text-muted-foreground">
                  {a.detail} · {a.occurrences} events · {a.affected_users} users · last {fmt(a.last_seen)}
                </p>
              </div>
              <button onClick={() => acknowledgeAlert(a)} className="rounded-md border px-2 py-1 text-xs font-semibold">
                Acknowledge
              </button>
            </div>
          ))}
        </section>
      )}

      {/* Section selector — scrolls inside its own container on mobile */}
      <div className="-mx-1 overflow-x-auto px-1">
        <div className="flex w-max gap-2 pb-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSectionKey(s.key)}
              className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-bold ${
                sectionKey === s.key ? "border-primary bg-primary text-primary-foreground" : "bg-background"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={range} onChange={(e) => setRange(e.target.value as any)} className="rounded-lg border bg-background px-2 py-1.5 text-sm">
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="rounded-lg border bg-background px-2 py-1.5 text-sm">
          {["all", "critical", "error", "warning", "info"].map((s) => <option key={s} value={s}>{s === "all" ? "All severities" : s}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border bg-background px-2 py-1.5 text-sm">
          <option value="all">All statuses</option>
          {INCIDENT_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="rounded-lg border bg-background px-2 py-1.5 text-sm">
          {["all", "android", "ios", "web", "server"].map((p) => <option key={p} value={p}>{p === "all" ? "All platforms" : p}</option>)}
        </select>
        <select value={version} onChange={(e) => setVersion(e.target.value)} className="rounded-lg border bg-background px-2 py-1.5 text-sm">
          <option value="all">All versions</option>
          {versions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Show resolved
        </label>
        <div className="relative min-w-[180px] flex-1">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Incident ID, code, route, correlation ID, U-XXXXXX"
            className="w-full rounded-lg border bg-background py-1.5 pl-8 pr-2 text-sm"
          />
        </div>
      </div>

      {sectionKey === "regression" ? (
        <ReleaseRegressionPanel since={since} />
      ) : (
        <>
          {sectionMetrics.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {sectionMetrics.map((p) => (
                <div key={p.metric} className="min-w-0 rounded-xl border p-3">
                  <p className="truncate text-xs font-bold uppercase text-muted-foreground">{p.metric.replace(/_/g, " ")}</p>
                  <p className={`text-xl font-black ${ratingClass(p.rating)}`}>{p.p95} ms p95</p>
                  <p className="break-words text-xs text-muted-foreground">
                    p50 {p.p50} ms · max {p.max} ms · {p.samples} samples · thresholds {p.threshold} ms
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Incident cards (mobile) */}
          <div className="space-y-2 sm:hidden">
            {groups.map((g) => {
              const inc = toIncident(g);
              const wf = workflowQ.data?.get(g.latest.group_hash ?? "");
              return (
                <div key={g.latest.id} className="rounded-xl border p-3">
                  <p className="break-all font-mono text-xs font-black">{g.latest.error_id}</p>
                  <p className="mt-0.5 break-words text-sm font-semibold">{g.latest.safe_message}</p>
                  <p className="mt-1 break-words text-[11px] text-muted-foreground">
                    <span className={`mr-1 rounded border px-1 ${sevClass(String(g.latest.severity))}`}>{g.latest.severity}</span>
                    {g.latest.category} · {g.latest.code} · {g.latest.stage ?? "—"} · {g.latest.route ?? g.latest.page ?? "—"}
                  </p>
                  <p className="mt-1 break-words text-[11px] text-muted-foreground">
                    {g.count}x · {g.users.size} users · {g.latest.app_version ?? "—"} / {g.latest.platform ?? "—"} ·{" "}
                    {wf?.status ?? "open"} · {fmt(g.latest.created_at)}
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button onClick={() => setSelected(inc)} className="rounded-md border px-2 py-1.5 text-xs font-bold">
                      View details
                    </button>
                    <button
                      onClick={() => copy(buildSafeFixPrompt({ incident: inc }), "Safe fix prompt")}
                      className="inline-flex items-center justify-center gap-1 rounded-md bg-primary px-2 py-1.5 text-xs font-bold text-primary-foreground"
                    >
                      <Copy className="h-3 w-3" /> Safe fix prompt
                    </button>
                  </div>
                </div>
              );
            })}
            {!groups.length && (
              <p className="rounded-xl border p-6 text-center text-sm text-muted-foreground">
                {eventsQ.isLoading ? "Loading…" : "No incidents recorded for these filters."}
              </p>
            )}
          </div>

          {/* Incident table (desktop) */}
          <div className="hidden overflow-x-auto rounded-xl border sm:block">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase">
                <tr>
                  <th className="p-2">Incident ID</th><th className="p-2">Summary</th><th className="p-2">Stage</th>
                  <th className="p-2">Count</th><th className="p-2">Users</th><th className="p-2">Status</th>
                  <th className="p-2">Version</th><th className="p-2">Last seen</th><th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const inc = toIncident(g);
                  const wf = workflowQ.data?.get(g.latest.group_hash ?? "");
                  return (
                    <tr key={g.latest.id} className="border-t align-top">
                      <td className="p-2 font-mono text-xs font-bold">
                        <span className="break-all">{g.latest.error_id}</span>
                      </td>
                      <td className="p-2">
                        <p className="break-words font-semibold">{g.latest.safe_message}</p>
                        <p className="break-words text-xs text-muted-foreground">
                          <span className={`mr-1 rounded border px-1 ${sevClass(String(g.latest.severity))}`}>{g.latest.severity}</span>
                          {g.latest.category} · {g.latest.code} · {g.latest.route ?? g.latest.page ?? "—"}
                        </p>
                      </td>
                      <td className="p-2 text-xs">{g.latest.stage ?? "—"}</td>
                      <td className="p-2 font-bold">{g.count}</td>
                      <td className="p-2">{g.users.size}</td>
                      <td className="p-2 text-xs">
                        {wf?.status?.replace(/_/g, " ") ?? "open"}
                        {wf?.recurred && <span className="ml-1 text-destructive">↻</span>}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {g.latest.app_version ?? "—"}<br />{g.latest.platform ?? "—"}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">{fmt(g.latest.created_at)}</td>
                      <td className="p-2">
                        <div className="flex flex-wrap gap-1">
                          <button onClick={() => setSelected(inc)} className="rounded-md border px-2 py-1 text-xs font-semibold">View</button>
                          <button
                            onClick={() => copy(buildInvestigationPrompt({ incident: inc }), "Investigation prompt")}
                            className="rounded-md border px-2 py-1 text-xs font-semibold"
                          >
                            Investigate
                          </button>
                          <button
                            onClick={() => copy(buildSafeFixPrompt({ incident: inc }), "Safe fix prompt")}
                            className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground"
                          >
                            <Copy className="h-3 w-3" /> Fix prompt
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!groups.length && (
                  <tr>
                    <td colSpan={9} className="p-6 text-center text-muted-foreground">
                      {eventsQ.isLoading ? "Loading…" : "No incidents recorded for these filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {slowRows.length > 0 && (
            <div className="overflow-x-auto rounded-xl border">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase">
                  <tr>
                    <th className="p-2">Slow operation</th><th className="p-2">Duration</th><th className="p-2">Rating</th>
                    <th className="p-2">Page</th><th className="p-2">Version</th><th className="p-2">When</th><th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {slowRows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="p-2 font-semibold">{r.metric}</td>
                      <td className="p-2">{r.duration_ms} ms</td>
                      <td className={`p-2 font-bold ${ratingClass(r.rating)}`}>{r.rating}</td>
                      <td className="p-2 break-all text-muted-foreground">{r.page ?? "—"}</td>
                      <td className="p-2 text-muted-foreground">{r.app_version ?? "—"} / {r.platform ?? "—"}</td>
                      <td className="p-2 text-muted-foreground">{fmt(r.created_at)}</td>
                      <td className="p-2">
                        <button
                          onClick={() =>
                            copy(
                              buildSafeFixPrompt({
                                incident: {
                                  error_id: `PERF-${r.id.slice(0, 8).toUpperCase()}`,
                                  category: "startup",
                                  code: "startup_slow",
                                  safe_message: `${r.metric} took ${r.duration_ms} ms`,
                                  page: r.page,
                                  route: r.page,
                                  stage: r.metric,
                                  duration_ms: r.duration_ms,
                                  app_version: r.app_version,
                                  platform: r.platform,
                                  severity: r.rating === "critical" ? "critical" : "warning",
                                  created_at: r.created_at,
                                  metadata: {},
                                },
                              }),
                              "Safe fix prompt",
                            )
                          }
                          className="rounded-md border px-2 py-1 text-xs font-semibold"
                        >
                          Fix prompt
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <DiagnosticsHealthCard />

      {selected && (
        <IncidentDetailsSheet
          incident={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {selected && (
        <button
          onClick={() => toggleResolved(selected)}
          className="fixed bottom-3 left-3 z-[60] inline-flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-xs font-semibold shadow"
        >
          <ShieldCheck className="h-4 w-4" /> {selected.resolved ? "Reopen group" : "Mark group resolved"}
        </button>
      )}

      <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Clock className="h-3 w-3" /> Detailed incidents and performance samples are kept 30 days; aggregated metrics 90
        days; release and baseline summaries long term. Financial audit records are separate and unaffected. No OTPs,
        tokens, keys, request bodies, QR images, UPI credentials or bet numbers are ever stored.
      </p>
    </div>
  );
}
